import { useEffect, useRef, useMemo, useCallback } from "react";
import { cellCenterMM, drawCircularImage, fitFontPx, CSS_FONT } from "@/lib/pinLayout";
import { Plus } from "lucide-react";
import { getProjectImageUrl } from "@/lib/imageCache";

export function PinCell({
  cell, index, layout, ppmm, selected, isNextEmpty, showCenter = false,
  centerDotSize = 2.4, centerDotColor = "#e22020", interactive = true,
  onSelect, onChangeTransform, projectId,
}) {
  const L = layout;
  const { x, y } = cellCenterMM(index, L);
  const outerPx = L.OUTER_D * ppmm;
  const innerPx = L.INNER_D * ppmm;
  const leftPx = (x - L.OUTER_D / 2) * ppmm;
  const topPx = (y - L.OUTER_D / 2) * ppmm;

  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dpr = typeof window !== "undefined" ? Math.min(Math.max((window.devicePixelRatio || 1) * 2, 2), 3) : 1;
  const canvasPx = Math.round(innerPx * dpr);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Rendering preview high-resolution khusus editor.
    // Tidak mengubah canvas PDF atau hasil cetak.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    drawCircularImage(ctx, img, canvasPx, cell.zoom || 1, cell.offX || 0, cell.offY || 0);
  }, [canvasPx, cell.zoom, cell.offX, cell.offY]);

  useEffect(() => {
    let cancelled = false;

    if (cell.type !== "image") {
      imgRef.current = null;
      return;
    }

    const loadImage = async () => {
      try {
        const src = cell.imageData ||
          (cell.imageId && projectId
            ? await getProjectImageUrl(projectId, cell.imageId)
            : null);

        if (!src || cancelled) return;

        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          imgRef.current = img;
          redraw();
        };
        img.onerror = () => {
          if (!cancelled) imgRef.current = null;
        };
        img.src = src;
      } catch (error) {
        console.error("[PinCell] Gagal memuat gambar:", error);
      }
    };

    imgRef.current = null;
    loadImage();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell.imageData, cell.imageId, cell.type, projectId]);

  useEffect(() => { redraw(); }, [redraw]);

  // pan drag
  const dragState = useRef(null);
  const onPointerDown = (e) => {
    if (!interactive || cell.type !== "image") return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, offX: cell.offX || 0, offY: cell.offY || 0 };
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const dxPx = e.clientX - dragState.current.startX;
    const dyPx = e.clientY - dragState.current.startY;
    const frac = 1 / innerPx;
    let nOffX = dragState.current.offX + dxPx * frac;
    let nOffY = dragState.current.offY + dyPx * frac;
    nOffX = Math.max(-1.5, Math.min(1.5, nOffX));
    nOffY = Math.max(-1.5, Math.min(1.5, nOffY));
    onChangeTransform?.({ offX: nOffX, offY: nOffY });
  };
  const onPointerUp = (e) => {
    dragState.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const fontPx = useMemo(
    () => (cell.type === "text"
      ? fitFontPx(cell.text, innerPx * 0.82, { fontFamily: cell.fontFamily, bold: cell.bold })
      : 12),
    [cell.type, cell.text, cell.fontFamily, cell.bold, innerPx]
  );

  return (
    <div className="absolute" style={{ left: leftPx, top: topPx, width: outerPx, height: outerPx }}>
      <div className="absolute inset-0 rounded-full"
        style={{ border: `${Math.max(1, 0.2 * ppmm)}px solid hsl(var(--cutline) / 0.65)` }} />
      {selected && (
        <div
          className="absolute rounded-full border-2 border-dashed animate-spin pointer-events-none"
          style={{
            inset: -6,
            borderColor: "hsl(var(--ring))",
            animationDuration: "8s"
          }}
        />
      )}
      {isNextEmpty && cell.type === "empty" && (
        <div className="absolute rounded-full"
          style={{ inset: -3, border: "2px dashed #e22020", opacity: 0.7 }} />
      )}
      <button
        type="button"
        data-testid={`pin-cell-${index}`}
        onClick={(e) => { e.stopPropagation(); onSelect?.(); }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={`group absolute flex items-center justify-center overflow-hidden rounded-full outline-none ${
          cell.type === "image" && interactive ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
        }`}
        style={{
          left: (outerPx - innerPx) / 2,
          top: (outerPx - innerPx) / 2,
          width: innerPx,
          height: innerPx,
          border: `${Math.max(1, 0.12 * ppmm)}px solid hsl(var(--guide) / 0.6)`,
          background:
            cell.type === "color"
              ? (cell.fillColor || "#cccccc")
              : cell.type === "empty"
              ? "#F1F2F4"
              : cell.type === "image"
              ? (cell.bgColor || "#FFFFFF")
              : "#F1F2F4",
          touchAction: "none",
        }}
      >
        {cell.type === "image" && (
          <canvas ref={canvasRef} width={canvasPx} height={canvasPx}
            style={{ width: innerPx, height: innerPx, pointerEvents: "none" }} />
        )}
        {cell.type === "text" && (
          <span className="px-1 text-center leading-tight"
            style={{
              fontSize: fontPx,
              fontFamily: CSS_FONT[cell.fontFamily] || CSS_FONT.sans,
              fontWeight: cell.bold ? 700 : 400,
              color: cell.color || "#111111",
              whiteSpace: "nowrap",
            }}>
            {cell.text}
          </span>
        )}
        {cell.type === "empty" && (
          <div className="flex items-center justify-center w-full h-full pointer-events-none">
            <svg
              viewBox="0 0 48 48"
              style={{ width: "58%", height: "58%" }}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect x="7" y="9" width="30" height="25" rx="5"
                fill="#E5EBF2" stroke="#91A1B5" strokeWidth="2.2"/>
              <circle cx="28" cy="16" r="3" fill="#91A1B5"/>
              <path d="M9 29L17 21L22 26L26 22L35 31"
                stroke="#91A1B5" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="35" cy="35" r="9" fill="#91A1B5"/>
              <path d="M35 30.5V39.5M30.5 35H39.5"
                stroke="#FFFFFF" strokeWidth="2.2"
                strokeLinecap="round"/>
            </svg>
          </div>
        )}
      </button>
      {/* optional colored dot at TOP, in the ring gap between inner & outer circle */}
      {showCenter && (() => {
        const midRpx = ((L.INNER_D / 2 + L.OUTER_D / 2) / 2) * ppmm;
        const dot = Math.max(3, (centerDotSize || 2.4) * ppmm);
        return (
          <div
            className="pointer-events-none absolute rounded-full"
            style={{
              left: outerPx / 2 - dot / 2,
              top: outerPx / 2 - midRpx - dot / 2,
              width: dot,
              height: dot,
              background: centerDotColor || "#e22020",
            }}
          />
        );
      })()}
    </div>
  );
}
